import { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Button, Card, Col, Form, Input, Row, Space, Table, theme, Tooltip } from 'antd';
import { getConsultationApi } from '@services/HealthClass';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import './HealthClassClue.less';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

const HealthClassClue = () => {
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [data, setData] = useState(undefined);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const isSearchRef = useRef(null);
    const [load, setLoad] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [filObj, setFilObj] = useState({
        phone: '',
    });
    const onFormFinish = () => {
        isSearchRef.current = true;
        setFilObj({
            phone: form.getFieldValue('phone') || '',
        });
    };

    const onReset = () => {
        form.resetFields();
        setFilObj({
            phone: '',
        });
        setPage(1);
        setPageSize(10);
    };

    const columns = [
        {
            title: '姓名',
            dataIndex: 'name',
            key: 'name',
            align: 'center',
            width: 100,
        },
        {
            title: '手机号',
            dataIndex: 'phone',
            key: 'phone',
            align: 'center',
        },
        {
            title: '单位名称',
            dataIndex: 'unitName',
            key: 'unitName',
            align: 'center',
            render: (_, row) => <>{row?.unitName ? <div>{row.unitName}</div> : <div>/</div>}</>,
        },
        {
            title: '职务',
            dataIndex: 'dutiesName',
            key: 'dutiesName',
            align: 'center',
            render: (_, row) => <>{row?.dutiesName ? <div>{row.dutiesName}</div> : <div>/</div>}</>,
        },
        {
            title: '团队人数',
            dataIndex: 'number',
            key: 'number',
            align: 'center',
            render: (_, row) => <>{row?.number ? <div>{row.number}人</div> : <div>/</div>}</>,
        },
        {
            title: '申请时间',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center',
            width: 180,
        },
    ];

    const getListData = useCallback(async (page, pageSize, phone) => {
        isSearchRef.current = false;
        const params = {
            page,
            pageSize,
            phone,
        };
        console.log(params);
        setLoad(true);
        const res = await getConsultationApi(params);
        if (res.status_code === 200) {
            setLoad(false);
            setData(res.data.list);
            setTotal(res.data.totalCount);
        }
    }, []);

    const onChangePage = (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
    };

    useEffect(() => {
        const { phone } = filObj;
        if (isSearchRef.current === true) {
            getListData(1, pageSize, phone);
        } else {
            getListData(page, pageSize, phone);
        }
    }, [page, pageSize, filObj, getListData]);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const handleRefresh = useCallback(() => {
        getListData(page, pageSize, filObj.phone);
    }, [getListData, page, pageSize, filObj.phone]);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    return (
        <div className="page-container list-page-container healthclass-clue-page">
            <PageHeader title="咨询用户" breadcrumb={[{ label: '健学云' }, { label: '线索管理' }, { label: '咨询用户' }]} />
            <Card
                size="small"
                className="healthclass-clue-filter-panel"
                style={{
                    marginBottom: token.marginLG,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    background: token.colorFillAlter,
                }}
            >
                <Form form={form} name="control-hooks">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item name="phone" label="手机号:">
                                <Input placeholder="请输入手机号" allowClear />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={8}>
                            <Form.Item label={<span>&nbsp;</span>} colon={false}>
                                <Space size="small">
                                    <Button type="primary" htmlType="submit" onClick={onFormFinish}>
                                        查询
                                    </Button>
                                    <Button onClick={onReset}>重置</Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            <div ref={tableRegionRef} className="table-region-container healthclass-clue-table-region">
                <div className="main-toolbar healthclass-clue-toolbar">
                    <div className="main-tools">
                        <span className="table-title">咨询用户列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap healthclass-clue-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always template-table' : 'template-table'}
                        columns={columns}
                        dataSource={data}
                        loading={load}
                        size="medium"
                        bordered
                        sticky
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangePage,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                        }}
                        rowKey={row => row.id}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>
        </div>
    );
};

export default HealthClassClue;
